import {
  dedupeOrganizationTypeValues,
  fetchAllOrganizationTypes,
  mapOrganizationTypeValues,
} from './organization-type-options.shared';
import { OnOptionsLoad } from './types';

/** Synthetic autocomplete value; stripped before API requests. */
export const ALL_HEALTHCARE_ORGANIZATION_TYPE_ID = -2;

export const ALL_HEALTHCARE_ORGANIZATION_TYPE_OPTION: {
  id: number;
  name: string;
} = {
  id: ALL_HEALTHCARE_ORGANIZATION_TYPE_ID,
  name: 'All Healthcare',
};

/**
 * Organization type IDs included when "All Healthcare" is selected.
 * Prefer IDs so renames do not drop members; names remain as a fallback.
 */
export const HEALTHCARE_ORGANIZATION_TYPE_IDS = [
  41, // Hospital
  9, // Medical Services
  45, // Inpatient Psychiatric Hospital/Facility
  46, // Higher Education – Medical Services
  29, // Nursing Home
  21, // Therapy/Counseling
] as const;

/** Organization type names included when "All Healthcare" is selected. */
export const HEALTHCARE_ORGANIZATION_TYPE_NAMES = [
  'Hospital',
  'Medical Services',
  /** Legacy label retained if still present in older environments. */
  'Medical sexual abuse',
  'Higher Education – Medical Services',
  'Higher Education - Medical Services',
  'Inpatient Psychiatric Hospital/Facility',
  'Therapy/Counseling',
  'Nursing Home',
] as const;

const normalizedHealthcareIds = new Set<number>(
  HEALTHCARE_ORGANIZATION_TYPE_IDS,
);

const normalizedHealthcareNames = new Set(
  HEALTHCARE_ORGANIZATION_TYPE_NAMES.map((name) => name.trim().toLowerCase()),
);

export const isAllHealthcareOrganizationTypeOption = (
  item: { id?: number | string } | null | undefined,
): boolean => Number(item?.id) === ALL_HEALTHCARE_ORGANIZATION_TYPE_ID;

export const isHealthcareOrganizationTypeName = (
  name: string | undefined,
): boolean => {
  if (!name) {
    return false;
  }

  return normalizedHealthcareNames.has(name.trim().toLowerCase());
};

export const isHealthcareOrganizationType = (orgType: {
  id?: number | string;
  name?: string;
}): boolean => {
  const id = Number(orgType.id);

  if (Number.isFinite(id) && normalizedHealthcareIds.has(id)) {
    return true;
  }

  return isHealthcareOrganizationTypeName(orgType.name);
};

export async function loadHealthcareOrganizationTypes(
  onOptionsLoad: OnOptionsLoad,
): Promise<Array<{ id: number; name: string }>> {
  const allOrgTypes = await fetchAllOrganizationTypes(onOptionsLoad);

  return mapOrganizationTypeValues(
    allOrgTypes.filter((orgType) => isHealthcareOrganizationType(orgType)),
  );
}

export async function resolveAllHealthcareOrganizationTypes(
  onOptionsLoad: OnOptionsLoad,
  currentValue: Array<{ id: number; name: string }> | undefined,
): Promise<Array<{ id: number; name: string }>> {
  const healthcareTypes = await loadHealthcareOrganizationTypes(onOptionsLoad);

  const withoutAllHealthcare = (currentValue ?? [])
    .filter((item) => !isAllHealthcareOrganizationTypeOption(item))
    .map((item) => ({
      id: typeof item.id === 'number' ? item.id : Number(item.id),
      name: item.name ?? '',
    }));

  return dedupeOrganizationTypeValues([
    ...withoutAllHealthcare,
    ...healthcareTypes,
  ]);
}
